// @ts-strict-ignore
import React, { memo, useRef } from 'react'
import { IRelayPaginationOptions, useRelayPagination } from 'hooks/relayPagination'
import { CircularProgress, Collapse } from '@mui/material'
import Condition from 'components/Condition'
import { makeStyles } from 'tss-react/mui'
import Button from '../Button'

interface IRelayPaginationProps<Filters> extends IRelayPaginationOptions<Filters> {
  noMoreResultsText?: string
  hideWhenNoMoreResults?: boolean
}

const useStyles = makeStyles()({
  container: {
    display: 'flex',
    justifyContent: 'center',
  },
})

/**
 * @param props.refetch refetch function from usePaginationFragment; the fragment MUST define the 'count' argument
 */
function RelayPagination<Filters>(props: IRelayPaginationProps<Filters>) {
  const { classes } = useStyles()
  const { currentCount, hasNext, totalCount, noMoreResultsText, hideWhenNoMoreResults } = props
  const ref = useRef<HTMLDivElement>(null)
  const {
    loading,
    nextLoadCount,
    moreCount,
    empty,
    isStreaming,
    infiniteScroll,
    loadNextPage,
  } = useRelayPagination({ ...props, ref })

  let text = noMoreResultsText || 'No more results'
  if (hasNext) text = 'Load more'
  if (nextLoadCount) text = `Load ${nextLoadCount} more`
  if (moreCount) text = `${text} (${moreCount} left)`

  if (!hasNext && (empty || hideWhenNoMoreResults)) return null

  return (
    <div className={classes.container} ref={ref}>
      <Condition condition={totalCount > 0 || currentCount > 0}>
        <div style={{ textAlign: 'center' }}>
          <Collapse in={!loading && (!infiniteScroll || !hasNext)}>
            <Button color="secondary" onClick={loadNextPage} disabled={!hasNext || loading || infiniteScroll}>
              {text}
            </Button>
          </Collapse>

          <Collapse in={loading || ((infiniteScroll || isStreaming) && hasNext)} mountOnEnter unmountOnExit>
            <CircularProgress size={48} thickness={4} />
          </Collapse>
        </div>
      </Condition>
    </div>
  )
}

export default memo(RelayPagination)
